import React, { useState, useEffect } from 'react';
import { X, AlertTriangle, Info, AlertCircle } from 'lucide-react';
import { getActiveAnnouncements, SiteAnnouncement } from '../firebase/moderation';

const SiteAnnouncements: React.FC = () => {
  const [announcements, setAnnouncements] = useState<SiteAnnouncement[]>([]);
  const [dismissedAnnouncements, setDismissedAnnouncements] = useState<Set<string>>(new Set());

  // Helper function to handle Firestore timestamps
  const convertTimestamp = (timestamp: Date | any): Date => {
    if (timestamp instanceof Date) {
      return timestamp;
    }
    if (timestamp && typeof timestamp.toDate === 'function') {
      return timestamp.toDate();
    }
    return new Date();
  };

  useEffect(() => {
    const loadAnnouncements = async () => {
      try {
        const activeAnnouncements = await getActiveAnnouncements();
        setAnnouncements(activeAnnouncements);
      } catch (error) {
        console.error('Error loading announcements:', error);
      }
    };

    loadAnnouncements();

    // Load dismissed announcements from localStorage
    const dismissed = localStorage.getItem('dismissedAnnouncements');
    if (dismissed) {
      try {
        setDismissedAnnouncements(new Set(JSON.parse(dismissed)));
      } catch (error) {
        console.error('Error loading dismissed announcements:', error);
      }
    }
  }, []);

  const dismissAnnouncement = (announcementId: string) => {
    const newDismissed = new Set(dismissedAnnouncements);
    newDismissed.add(announcementId);
    setDismissedAnnouncements(newDismissed);
    localStorage.setItem('dismissedAnnouncements', JSON.stringify(Array.from(newDismissed)));
  };

  const getIcon = (type: 'info' | 'warning' | 'emergency') => {
    switch (type) {
      case 'emergency':
        return <AlertTriangle className="w-5 h-5 text-red-400" />;
      case 'warning':
        return <AlertCircle className="w-5 h-5 text-yellow-400" />;
      default:
        return <Info className="w-5 h-5 text-accent-blue" />;
    }
  };

  const getBackgroundColor = (type: 'info' | 'warning' | 'emergency') => {
    switch (type) {
      case 'emergency':
        return 'bg-red-900 border-red-600';
      case 'warning':
        return 'bg-yellow-900 border-yellow-600';
      default:
        return 'bg-accent-blue-strong border-accent-blue';
    }
  };

  const visibleAnnouncements = announcements.filter(
    announcement => !dismissedAnnouncements.has(announcement.id)
  );

  if (visibleAnnouncements.length === 0) {
    return null;
  }

  return (
    <div className="fixed top-20 md:top-4 left-4 right-4 z-50 space-y-2">
      {visibleAnnouncements.map(announcement => (
        <div
          key={announcement.id}
          className={`${getBackgroundColor(announcement.type)} border rounded-lg p-4 shadow-lg`}
        >
          <div className="flex items-start justify-between">
            <div className="flex items-start">
              {getIcon(announcement.type)}
              <div className="ml-3">
                <h3 className="text-white font-semibold">{announcement.title}</h3>
                <p className="text-gray-200 text-sm mt-1">{announcement.message}</p>
                <p className="text-gray-300 text-xs mt-2">
                  By {announcement.createdByName} • {convertTimestamp(announcement.createdAt).toLocaleString()}
                </p>
              </div>
            </div>
            <button
              onClick={() => dismissAnnouncement(announcement.id)}
              className="text-gray-300 hover:text-white ml-4"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default SiteAnnouncements;
